import type { ConcernState } from '@atlas/ui-components'
import type { DeliverableFile } from '@/domain/work-order.types'
import type { UploadItem } from '@/upload/upload-store'
import { isConfirmedStatus, isPreUploadFailure, UploadItemStatus } from '@/upload/upload-store'

/**
 * One row in a deliverable box, whether the file is still uploading or already
 * on the server.
 *
 * The same shape covers both so a single card renders the asset for its whole
 * life. The upload fields are absent once the server row takes over.
 */
export interface DeliverableRow {
	/** Stable across the file's whole life — known from `requestUploadUrl`, before any byte is sent. */
	key: string
	fileId: string | null
	fileName: string
	contentType?: string
	thumbnailUrl?: string
	/** Local blob preview, shown until the server thumbnail exists. */
	file?: File
	previewUrl?: string
	/** Present only while this row is an in-flight upload. */
	upload?: {
		itemId: string
		progress: number
		isConfirming: boolean
		isConfirmed: boolean
		rejectedReason?: string
	}
	concerns: Record<string, ConcernState>
}

/**
 * Server rows plus the uploads not yet among them, as one list.
 *
 * Keyed by `fileId`, which exists from the moment the upload URL is issued — so
 * an upload and the server row that later represents it are the same row, and
 * the card is never swapped for a different component halfway through. That swap
 * is what this replaces: a tile was dismissed once its file appeared in the
 * server list, leaving a window where both or neither could be showing, plus
 * reflow as one unmounted and the other mounted.
 *
 * Server rows come first and keep their order (the persisted `deliverable_order`
 * for visuals); in-flight uploads append in the order they were enqueued, so a
 * row never changes position as it progresses.
 */
export function mergeDeliverables(serverFiles: readonly DeliverableFile[], uploads: readonly UploadItem[]) {
	const rows: DeliverableRow[] = serverFiles.map((file) => ({
		key: file.fileId,
		fileId: file.fileId,
		fileName: file.fileName ?? file.fileId,
		contentType: file.contentType,
		thumbnailUrl: file.thumbnailUrl,
		concerns: {},
	}))

	const onServer = new Set(serverFiles.map((file) => file.fileId))
	for (const item of uploads) {
		// Already listed by the server: that row is authoritative — it carries the
		// persisted order and the presigned thumbnail. Dropping the upload item here
		// is the whole handover, and it needs no dismissal call or effect.
		if (item.fileId && onServer.has(item.fileId)) continue
		rows.push({
			key: item.fileId ?? item.id,
			fileId: item.fileId,
			fileName: item.fileName,
			contentType: item.contentType,
			file: item.file,
			previewUrl: item.previewUrl,
			upload: {
				itemId: item.id,
				progress: item.progress,
				isConfirming: item.status === UploadItemStatus.CONFIRMING,
				isConfirmed: isConfirmedStatus(item.status),
				rejectedReason: isPreUploadFailure(item.status) ? (item.error ?? 'Upload failed') : undefined,
			},
			concerns: item.concerns,
		})
	}
	return rows
}
